import React, { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { fetchOtherResponsibilities } from "../../api/filters";
import { LetterTranslator } from "../../utilities/letter-translator";

interface Option {
  id: number | null;
  name: string;
}

interface OtherResponsibilitiesCustomComponentProps {
  setSearchValue: (
    value: { otherResponsibilityID: number | null; otherResponsibilityName: string }[]
  ) => void;
  searchTitle: string;
  optionsData: Option[];
  updateOtherRespoOptions: (otherR: { id: number; name: string }[]) => void;
  getOptionLabel: (option: Option) => string;
  getOptionId: (option: Option) => number | null;
  handleBlur?: () => void;
  value?: any;
  error?: boolean;
  helperText?: string | undefined;
}

const filterOptions = (options: Option[], state: any) => {
  const inputValue = state.inputValue.toLowerCase();
  const latValue = LetterTranslator.cyr2lat(inputValue);
  const cyrValue = LetterTranslator.lat2cyr(inputValue);

  return options.filter((option: Option) => {
    const optionValue = option.name.toLowerCase();
    return (
      optionValue.includes(inputValue) ||
      optionValue.includes(cyrValue) ||
      optionValue.includes(latValue)
    );
  });
};

const OtherResponsibilitiesCustomComponent: React.FC<OtherResponsibilitiesCustomComponentProps> = ({
  setSearchValue,
  searchTitle,
  optionsData,
  updateOtherRespoOptions,
  getOptionLabel,
  getOptionId,
  handleBlur,
  error,
  helperText,
  value,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [options, setOptions] = useState<Option[]>(optionsData);

  const handleAddOption = () => {
    if (inputValue && !options.some((option) => getOptionLabel(option) === inputValue)) {
      const newOption: Option = { id: null, name: inputValue };
      setOptions([...options, newOption]);

      const newBackendOption = {
        otherResponsibilityID: newOption.id,
        otherResponsibilityName: newOption.name,
      };
      setSearchValue([
        ...options.map((option) => ({
          otherResponsibilityID: getOptionId(option),
          otherResponsibilityName: getOptionLabel(option),
        })),
        newBackendOption,
      ]);

      setInputValue("");
    }
  };

  const handleChange = (event: any, newValue: (string | Option)[]) => {
    const validOptions = newValue.map((value) => {
      if (typeof value === "string") {
        return { otherResponsibilityID: null, otherResponsibilityName: value };
      }
      return {
        otherResponsibilityID: getOptionId(value),
        otherResponsibilityName: getOptionLabel(value),
      };
    });

    setSearchValue(validOptions);
  };

  const updateOptions = async () => {
    const response = await fetchOtherResponsibilities(1, 50, inputValue);
    const o = response.responsibilities as { id: number; name: string }[];
    updateOtherRespoOptions(o);
  };

  useEffect(() => {
    setOptions(optionsData);
  }, [optionsData]);

  return (
    <Autocomplete
      multiple
      value={value}
      options={options}
      filterOptions={filterOptions}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }
        return getOptionLabel(option);
      }}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        if (newInputValue.length < 1) {
          setOptions(optionsData);
        }
        if (newInputValue?.length > 1) {
          updateOptions();
        }
        setInputValue(newInputValue);
      }}
      onBlur={handleBlur}
      freeSolo
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          handleAddOption();
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={searchTitle}
          error={error}
          helperText={helperText}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={getOptionId(option)}>
          {getOptionLabel(option)}
        </li>
      )}
    />
  );
};

export default OtherResponsibilitiesCustomComponent;
